import { Button, Form, Frequency, Stack, Text, useBasisForm } from "basis";
import { FormActions } from "components/FormActions/FormActions";
import { Layout } from "components/Layout/Layout";
import { getRegional } from "library/getRegional";
import { getNumberOfSteps } from "pages/HardshipApplication/reducer/hardshipSelectors";
import { usePageTracking } from "react-event-tracker";
import { useHardshipApplicationContext } from "../hooks/useHardshipApplicationContext";
import { useNav } from "../hooks/useNav";
import { ActionTypes, ExpensesState } from "../reducer/types";

export const Expenses = () => {
  const { state, dispatch } = useHardshipApplicationContext();
  const { expenses } = state;

  const { methods, Field } = useBasisForm<ExpensesState>({
    defaultValues: expenses,
  });

  const { navigate, goBack } = useNav();

  const onSubmit = (values: ExpensesState) => {
    dispatch({ type: ActionTypes.COMPLETE_EXPENSES, ...values });
    navigate("review");
  };

  const isNZ = getRegional("code") === "NZ";

  const numberOfSteps = getNumberOfSteps(state);

  usePageTracking({
    page: {
      pageType: "input-page",
      siteSection: "hardship",
      siteSubSection: "expenses",
      pageTitle: "What do you spend?",
    },
    user: {
      authenticationStatus: "not logged in",
    },
  });

  return (
    <Layout>
      <Layout.Header>Hardship Care application</Layout.Header>
      <Layout.Main>
        <Form methods={methods} onSubmit={onSubmit}>
          <Stack gap="8">
            <Stack gap="4">
              <Text>
                <strong>Step 6 of {numberOfSteps}</strong>
              </Text>
              <Text as="h2" textStyle="heading4">
                What do you spend?
              </Text>
              <Text>
                Help us understand your regular expenses by filling in each of
                the fields below.{" "}
                <strong>If it doesn’t apply to you, please enter ‘0’.</strong>
              </Text>
            </Stack>
            <Stack gap="8" width="100%" width-sm="400">
              <Text as="h3" textStyle="heading5" color="primary.blue.t100">
                Residential
              </Text>
              <Field
                label="Food/Groceries/Household"
                name="residential.foodGroceriesHousehold"
                mode="select"
                amountPrefix="$"
                as={Frequency}
              />
              <Field
                label="Council Rates"
                name="residential.councilRates"
                mode="select"
                amountPrefix="$"
                as={Frequency}
              />
              <Field
                label="Electricity"
                name="residential.electricity"
                mode="select"
                amountPrefix="$"
                as={Frequency}
              />
              <Field
                label="Gas"
                name="residential.gas"
                mode="select"
                amountPrefix="$"
                as={Frequency}
              />
              <Field
                label="Water"
                name="residential.water"
                mode="select"
                amountPrefix="$"
                as={Frequency}
              />
              <Field
                label="Landline/Internet/Cable TV"
                name="residential.landlineInternetCableTV"
                mode="select"
                amountPrefix="$"
                as={Frequency}
              />
              <Text as="h3" textStyle="heading5" color="primary.blue.t100">
                Other
              </Text>
              <Field
                label="Mobile phone"
                name="other.mobilePhone"
                mode="select"
                amountPrefix="$"
                as={Frequency}
              />
              <Field
                label="Medical/Pharmaceutical"
                name="other.medicalPharmaceutical"
                mode="select"
                amountPrefix="$"
                as={Frequency}
              />
              <Field
                label="Clothes/Shoes"
                name="other.clothesShoes"
                mode="select"
                amountPrefix="$"
                as={Frequency}
              />
              <Field
                label="Petrol"
                name="other.petrol"
                mode="select"
                amountPrefix="$"
                as={Frequency}
              />
              <Field
                label="Car registration/maintenance"
                name="other.carRegistrationMaintenance"
                mode="select"
                amountPrefix="$"
                as={Frequency}
              />
              <Field
                label="Public transport"
                name="other.publicTransport"
                mode="select"
                amountPrefix="$"
                as={Frequency}
              />
              <Field
                label="Child care"
                name="other.childCare"
                mode="select"
                amountPrefix="$"
                as={Frequency}
              />
              <Field
                label="Child maintenance"
                name="other.childMaintenance"
                mode="select"
                amountPrefix="$"
                as={Frequency}
              />
              <Field
                label="School/Education"
                name="other.educationExpenses"
                mode="select"
                amountPrefix="$"
                as={Frequency}
              />
              {isNZ && (
                <Field
                  label="Donations"
                  name="other.donations"
                  mode="select"
                  amountPrefix="$"
                  as={Frequency}
                />
              )}
              <Field
                label="Home/Contents Insurance"
                name="other.homeContentsInsurance"
                mode="select"
                amountPrefix="$"
                as={Frequency}
              />
              <Field
                label="Car insurance"
                name="other.carInsurance"
                mode="select"
                amountPrefix="$"
                as={Frequency}
              />
              <Field
                label="Health insurance"
                name="other.healthInsurance"
                mode="select"
                amountPrefix="$"
                as={Frequency}
              />
              <Field
                label="Life insurance"
                name="other.lifeInsurance"
                mode="select"
                amountPrefix="$"
                as={Frequency}
              />
              <Field
                label="Other insurance"
                name="other.otherInsurance"
                mode="select"
                amountPrefix="$"
                as={Frequency}
              />
              <Field
                label="Other (e.g. Sports, hobbies, etc.)"
                name="other.otherHobbies"
                mode="select"
                amountPrefix="$"
                as={Frequency}
              />
              <FormActions>
                <FormActions.Primary>
                  <Button variant="primary" type="submit">
                    Next
                  </Button>
                </FormActions.Primary>
                <FormActions.Secondary>
                  <Button variant="secondary" onClick={goBack}>
                    Back
                  </Button>
                </FormActions.Secondary>
              </FormActions>
            </Stack>
          </Stack>
        </Form>
      </Layout.Main>
    </Layout>
  );
};
